import React, { useState } from "react";

const Tiers = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");

  // Plan data mapped to Jibu Labs
  const plans = [
    {
      id: "explorer",
      name: "Explorer",
      tagline: "Startups, developers, and curious builders testing agents.",
      price: "Free forever",
      isFree: true,
      buttonText: "Get started",
      isPopular: false,
      features: [
        "5 AI Agent Operations / mo",
        "100 Playground Generations",
        "Basic developer API token",
        "Community support access",
        "50 AI pathfinder queries per month"
      ]
    },
    {
      id: "pathfinder",
      name: "Pathfinder",
      tagline: "Growing teams deploying production agent workflows.",
      price: billingCycle === "monthly" ? "$499/mo" : "$399/mo",
      buttonText: "Get started",
      isPopular: false,
      features: [
        "Everything in explorer, plus:",
        "Unlimited AI Agent Operations",
        "Custom RAG Connection (10GB)",
        "Autonomous Multi-Agent Networks",
        "Priority Slack & Email support"
      ]
    },
    {
      id: "strategist",
      name: "Strategist",
      tagline: "Enterprises scaling fine-tuned models and private VPCs.",
      price: billingCycle === "monthly" ? "$1,999/mo" : "$1,599/mo",
      buttonText: "Get started",
      isPopular: true,
      features: [
        "Everything in pathfinder, plus:",
        "Bespoke Neural Network training",
        "Dedicated GPU node routing",
        "Private VPC / On-Prem deployment",
        "Advanced AI Guardrails & Safety audits",
        "1 Free 30-min mentor consultation monthly"
      ]
    },
    {
      id: "enterprise",
      name: "Enterprise",
      tagline: "Bespoke custom pipelines and dedicated compute routing.",
      price: "Bespoke pricing",
      buttonText: "Contact sales",
      isPopular: false,
      features: [
        "Everything in strategist, plus:",
        "Dedicated engineering dashboard",
        "Custom branded workspaces",
        "Unlimited compute scaling bounds",
        "Dedicated account manager",
        "Talent analytics for counselors"
      ]
    }
  ];

  return (
    <section className="py-16 px-6 max-w-7xl mx-auto text-zinc-800 dark:text-zinc-200">
      {/* Headings */}
      <div className="text-center mb-12 space-y-4">
        <span className="text-xs font-semibold text-forest uppercase tracking-widest block">Our pricing</span>
        <h2 className="text-4xl font-extrabold text-zinc-900 dark:text-white tracking-tight">
          Choose your plan
        </h2>

        {/* Toggle Switch */}
        <div className="inline-flex p-1 rounded-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 gap-1 mt-4">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-6 py-2 rounded-full text-xs font-semibold transition-all duration-300 ${
              billingCycle === "monthly" 
                ? "bg-forest text-white shadow" 
                : "text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-250"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-6 py-2 rounded-full text-xs font-semibold transition-all duration-300 ${
              billingCycle === "annual" 
                ? "bg-forest text-white shadow" 
                : "text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-250"
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      {/* 4 Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch mb-16">
        {plans.map((p) => (
          <div
            key={p.id}
            className={`flex flex-col justify-between p-8 rounded-3xl border transition-all duration-300 relative ${
              p.isPopular
                ? "bg-forest text-white border-forest shadow-xl lg:scale-[1.03] z-10"
                : "bg-white dark:bg-zinc-900/60 border-zinc-200 dark:border-zinc-800 hover:border-forest/30"
            }`}
          >
            {p.isPopular && (
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-orangeAccent text-white text-[10px] font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow">
                Popular
              </div>
            )}

            <div>
              <h3 className="text-2xl font-bold mb-3">{p.name}</h3>
              <p className={`text-xs font-light mb-6 leading-relaxed ${
                p.isPopular ? "text-emerald-100" : "text-zinc-500 dark:text-zinc-400"
              }`}>
                {p.tagline}
              </p>
              <div className="mb-8">
                <span className="text-3xl font-extrabold tracking-tight">{p.price}</span>
              </div>
            </div>

            <div>
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("open-questionnaire", { detail: { plan: p.name } }));
                }}
                className={`w-full py-3 px-4 rounded-full font-bold text-xs transition-colors duration-200 border ${
                  p.isPopular
                    ? "bg-transparent text-white border-white hover:bg-white/10"
                    : "bg-transparent text-zinc-800 dark:text-white border-zinc-250 dark:border-zinc-750 hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                }`}
              >
                {p.buttonText}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Features Checklist Grid below the cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 border-t border-zinc-200 dark:border-zinc-800/80 pt-12">
        {plans.map((p) => (
          <div key={p.id} className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-2">
              {p.name} Features
            </h4>
            <ul className="space-y-3">
              {p.features.map((f, index) => (
                <li key={index} className="flex items-start space-x-3 text-xs leading-normal">
                  <span className="text-forest text-sm font-bold flex-shrink-0">✓</span>
                  <span className="text-zinc-650 dark:text-zinc-400 font-light">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Tiers;
